import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ColDef, ColumnApi, ColumnMovedEvent, ColumnPinnedEvent, ColumnPinnedType, ColumnResizedEvent, GetMainMenuItemsParams } from 'ag-grid-community'
import { debounce } from 'lodash-es'
import { gridColumnsQueryKeys, useGetGridColumns } from '@/services/auth/useAuthQueries'
import { callToSaveGridColumns } from '@/services/auth/apis'
import { GridColumnState } from '@/services/auth/gridColumn.types'
import {
  GRID_COLUMNS_LEGACY_STORAGE_PREFIX,
  GRID_COLUMNS_RESET_MENU_LABEL,
  GRID_COLUMNS_SAVE_DEBOUNCE_MS,
  GRID_COLUMNS_USER_EVENT_SOURCES,
} from '@/services/auth/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'

type Params<T> = {
  columnDefs: ColDef<T>[] | null | undefined
  gridKey?: string
  legacyStorageKey?: string
}

const colIdOf = <T>(col: ColDef<T>) => col.colId ?? col.field

const toPinned = (pinned: ColumnPinnedType | undefined): GridColumnState['pinned'] => {
  if (pinned === 'left' || pinned === 'right') return pinned
  return pinned === true ? 'left' : null
}

/** 저장값 순서대로 정렬하고 width·pinned 를 덮어쓴다. 화면에 없는 colId 는 버리고, 저장값에 없는 컬럼은 뒤에 붙인다. */
export const mergeColumnDefs = <T>(columnDefs: ColDef<T>[], saved: GridColumnState[]): ColDef<T>[] => {
  if (saved.length === 0) return columnDefs

  const used = new Set<ColDef<T>>()
  const sorted = saved.flatMap((state) => {
    const col = columnDefs.find((c) => colIdOf(c) === state.colId)
    if (!col || used.has(col)) return []
    used.add(col)
    return [{ ...col, width: state.width ?? col.width, pinned: state.pinned }]
  })

  return [...sorted, ...columnDefs.filter((col) => !used.has(col))]
}

// LOGISPRD-15456 — GI23~26 이 쓰던 localStorage 값([{ field, headerName, width, pinned }])을 저장 형식으로 바꾼다.
// ponytail: 지우지도 쓰지도 않는다 — 배포 전 배치를 서버 저장값이 없는 화주의 초기값으로만 쓴다. 이관이 끝났다고 보면 이 함수와 legacyStorageKey 를 지운다.
export const readLegacyColumns = (legacyStorageKey?: string): GridColumnState[] => {
  if (!legacyStorageKey) return []

  try {
    const saved = JSON.parse(localStorage.getItem(`${GRID_COLUMNS_LEGACY_STORAGE_PREFIX}-${legacyStorageKey}`) || '[]') as {
      field?: string
      width?: number
      pinned?: ColumnPinnedType
    }[]
    return saved.flatMap((state) => (state.field ? [{ colId: state.field, width: state.width ?? null, pinned: toPinned(state.pinned) }] : []))
  } catch {
    return []
  }
}

/**
 *  @hook useTableColumnDefs
 *  - gridKey 가 있으면 개인 컬럼 배치(순서·너비·고정)를 사용자·화주별로 서버에서 읽어 columnDefs 에 반영하고, 사용자가 바꾸면 서버에 저장한다.
 *  - gridKey 가 없으면 columnDefs 를 그대로 돌려주고 아무것도 저장하지 않는다.
 */
export const useTableColumnDefs = <T>({ columnDefs, gridKey, legacyStorageKey }: Params<T>) => {
  // 화주 전환은 페이지를 새로 읽으므로(MainHeader onConvert) 마운트 동안 바뀌지 않는다.
  const shipperCode = useMemo(() => AuthStorage.user.get().shipperCode ?? '', [])
  const queryClient = useQueryClient()

  const legacyColumns = useMemo(() => readLegacyColumns(legacyStorageKey), [legacyStorageKey])
  const { data: serverState } = useGetGridColumns(gridKey, shipperCode, legacyColumns)

  // 화면에서 바꾼 값 — 저장 응답을 기다리지 않고 바로 반영해, 리렌더로 columnDefs 가 다시 내려와도 배치가 되돌아가지 않게 한다.
  const [localState, setLocalState] = useState<GridColumnState[]>()
  // 되돌리기는 columnDefs 가 기본값으로 반영된 뒤 resetColumnState 를 불러야 width 를 정하지 않은 컬럼까지 기본 너비로 돌아간다.
  const [resetTarget, setResetTarget] = useState<ColumnApi | null>(null)

  // 앞선 PUT 이 끝난 뒤 다음 PUT 을 보낸다 — 되돌리기 직전에 나간 저장이 되돌리기보다 늦게 도착해 덮어쓰지 않게.
  const putQueue = useRef<Promise<unknown>>(Promise.resolve())
  const put = useMemo(
    () => (items: GridColumnState[]) => {
      if (!gridKey || !shipperCode) return
      putQueue.current = putQueue.current.then(() => callToSaveGridColumns(gridKey, shipperCode, items)).catch(() => undefined)
    },
    [gridKey, shipperCode],
  )
  const save = useMemo(() => debounce(put, GRID_COLUMNS_SAVE_DEBOUNCE_MS), [put])

  useEffect(() => () => save.flush(), [save])

  useEffect(() => {
    if (!resetTarget) return
    resetTarget.resetColumnState()
    setResetTarget(null)
  }, [resetTarget])

  const mergedColumnDefs = useMemo(() => {
    if (!gridKey || !columnDefs) return columnDefs
    return mergeColumnDefs(columnDefs, localState ?? serverState ?? [])
  }, [gridKey, columnDefs, localState, serverState])

  const gridProps = useMemo(() => {
    if (!gridKey) return {}

    const applyState = (items: GridColumnState[]) => {
      setLocalState(items)
      queryClient.setQueryData(gridColumnsQueryKeys.detail(gridKey, shipperCode), items)
    }

    const onColumnStateChanged = (event: ColumnMovedEvent<T> | ColumnResizedEvent<T> | ColumnPinnedEvent<T>) => {
      if (!GRID_COLUMNS_USER_EVENT_SOURCES.includes(event.source)) return
      // 끄는 동안의 중간 이벤트는 건너뛰고 끝났을 때만 반영한다.
      if ('finished' in event && !event.finished) return

      // 체크박스·No 처럼 그리드가 붙인 열은 colId 가 고정이 아니라 저장하지 않는다.
      const colIds = new Set((columnDefs ?? []).map(colIdOf))
      const items = event.columnApi
        .getColumnState()
        .filter((state) => colIds.has(state.colId))
        .map((state) => ({ colId: state.colId, width: state.width ?? null, pinned: toPinned(state.pinned) }))

      applyState(items)
      save(items)
    }

    const resetColumns = (columnApi: ColumnApi) => {
      // legacyStorageKey 그리드는 [] 로 지우면 다음 조회에서 localStorage 배치가 다시 올라가므로 기본 배치를 저장한다.
      const items = legacyColumns.length
        ? (columnDefs ?? []).flatMap((col) => {
            const colId = colIdOf(col)
            return colId ? [{ colId, width: col.width ?? null, pinned: toPinned(col.pinned) }] : []
          })
        : []

      save.cancel()
      applyState(items)
      setResetTarget(columnApi)
      put(items)
    }

    return {
      onColumnMoved: onColumnStateChanged,
      onColumnResized: onColumnStateChanged,
      onColumnPinned: onColumnStateChanged,
      // ag-grid 기본 '컬럼 초기화'(resetColumns)는 저장값이 반영된 columnDefs 로 돌아갈 뿐이라 이 항목으로 바꾼다.
      getMainMenuItems: (params: GetMainMenuItemsParams<T>) => [
        ...params.defaultItems.filter((item) => item !== 'resetColumns'),
        'separator',
        { name: GRID_COLUMNS_RESET_MENU_LABEL, action: () => resetColumns(params.columnApi) },
      ],
    }
  }, [gridKey, shipperCode, columnDefs, legacyColumns, save, put, queryClient])

  return { columnDefs: mergedColumnDefs, gridProps }
}
